import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import LiveGamesPreview from './components/LiveGamesPreview';
import TrustIndicators from './components/TrustIndicators';
import VideoTestimonials from './components/VideoTestimonials';
import SocialProofMetrics from './components/SocialProofMetrics';
import WhatsAppWidget from './components/WhatsAppWidget';

const HomepageRoyalWelcomeHub = () => {
  return (
    <>
      <Helmet>
        <title>Nitin Online Hub | Nitin Online Book - India's Best Gaming Platform</title>
        <meta 
          name="description" 
          content="Discover India's most trusted online gaming platform. Experience secure gaming, instant payouts, and personalized WhatsApp support. Join thousands of players for an exciting and rewarding gaming journey with Nitin Online Hub." 
        />
        <meta name="keywords" content="official Nitin Online Hub, official Nitin Online Book, Nitin online book login, Nitin online book sign up, Nitin online book app download, secure gaming India, best online betting platform" />
        <meta property="og:title" content="Nitin Online Hub | Nitin Online Book - India's Best Gaming Platform" />
        <meta property="og:description" content="Welcome to Nitin Online Hub and Nitin Online Book, India's leading online gaming platform. Experience premium gaming with Nitin online book login, sign up, app download. Secure transactions, instant payouts, 24/7 WhatsApp support." />
        <meta property="og:image" content="https://nitinonlinehub.in/assets/images/logo.png" />
        <meta property="og:url" content="https://nitinonlinehub.in/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Nitin Online Hub" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Nitin Online Hub | Nitin Online Book - India's Best Gaming Platform" />
        <meta name="twitter:description" content="Welcome to Nitin Online Hub and Nitin Online Book, India's leading online gaming platform. Experience premium gaming with Nitin online book login, sign up, app download. Secure transactions, instant payouts, 24/7 WhatsApp support." />
        <meta name="twitter:image" content="https://nitinonlinehub.in/assets/images/logo.png" />
        <link rel="canonical" href="https://nitinonlinehub.in/" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "Nitin Online Hub",
              "url": "https://nitinonlinehub.in/",
              "description": "India's most trusted online gaming platform offering secure gaming, instant payouts, and personalized customer support",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://nitinonlinehub.in/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Nitin Online Hub",
                "url": "https://nitinonlinehub.in/",
                "description": "Leading online gaming platform in India with a focus on security, fair play, and customer satisfaction"
              }
            }
          `}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is Nitin Online Hub?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Nitin Online Hub is India's premier online gaming platform offering secure betting, instant payouts, and premium gaming experiences through Nitin Online Book."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I sign up for Nitin Online Book?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "You can sign up for Nitin Online Book by contacting our WhatsApp support at +91-7704070169 or visiting our VIP contact page for personalized assistance."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Nitin Online Hub safe and secure?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Nitin Online Hub prioritizes security with encrypted transactions, verified players, and 24/7 customer support to ensure a safe gaming environment."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I download the Nitin Online Book app?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Contact our WhatsApp support team for the latest Nitin Online Book app download link and installation instructions."
                  }
                }
              ]
            }
          `}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section with Animated Casino Elements */}
        <HeroSection />
        
        {/* Authentic Content Section */}
        <section className="py-16 bg-gradient-to-r from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Trusted and Verified: Nitin Online Hub & Nitin Online Book Official Portal
              </h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                This is the official website for Nitin Online Hub and Nitin Online Book, with trusted support for login, sign-up, and app download. We deliver a transparent, secure, and player-first experience that helps you play smarter and win with confidence.
              </p>
              <ul className="list-disc list-inside text-gray-300 max-w-4xl mx-auto mt-6 space-y-3 text-left">
                <li><strong>Nitin online book login</strong> made simple: instant account access with 24/7 WhatsApp assistance.</li>
                <li><strong>Nitin online book sign up</strong>: secure onboarding, KYC-friendly, no hidden charges, and special welcome bonuses.</li>
                <li><strong>Nitin online book app download</strong>: fast, safe native apps for Android and iOS with the latest game updates and real-time notifications.</li>
              </ul>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <h3 className="text-xl font-semibold text-white mb-3">Easy Account Access</h3>
                <p className="text-gray-300">Getting started is simple. Create your account and dive into the action immediately with our user-friendly platform designed for both beginners and experienced players.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <h3 className="text-xl font-semibold text-white mb-3">Mobile Gaming Anywhere</h3>
                <p className="text-gray-300">Take your gaming experience on the go with our mobile app. Whether you're on Android or iOS, enjoy the same premium features and smooth gameplay from anywhere.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <h3 className="text-xl font-semibold text-white mb-3">Join the Community</h3>
                <p className="text-gray-300">Become part of India's largest gaming community. Connect with fellow players, share experiences, and enjoy exclusive benefits reserved for our valued members.</p>
              </div>
            </div>
            <div className="text-center mt-12">
              <p className="text-gray-400 max-w-4xl mx-auto">
                Experience the difference with the official Nitin Online Hub and Nitin Online Book website. Our proven fair-play system, quick withdrawals, and friendly 24/7 support help serious players win with confidence.
              </p>
            </div>
          </div>
        </section>
        
        {/* Live Games Preview with Real-time Player Counts */}
        <LiveGamesPreview />
        
        {/* Trust Indicators and Security Badges */}
        <TrustIndicators />
        
        {/* Video Testimonials from Real Winners */}
        <VideoTestimonials />
        
        {/* Live Social Proof Metrics */}
        <SocialProofMetrics />
        
        {/* Floating WhatsApp Widget */}
        <WhatsAppWidget />

        {/* Internal Links Example - Add to relevant components */}
        {/* <Link to="/about-dynasty-brand-heritage">Learn more about Nitin Online Hub</Link> */}
      </div>
    </>
  );
};

export default HomepageRoyalWelcomeHub;